/**
 * BuyerStatsPage - Statistiche per Buyer
 * Include: Panoramica acquisti, Pagamenti, Storico Transazioni, Fornitori preferiti
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { formatCurrency, formatNumber, getErrorMessage } from '@/lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import {
  TrendingUp,
  TrendingDown,
  Download,
  ShoppingCart,
  DollarSign,
  Package,
  Users,
  Calendar,
  BarChart3,
  Wallet,
  History,
  Building2,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  FileSpreadsheet,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Colori per i grafici
const COLORS = ['#16a34a', '#2563eb', '#7c3aed', '#db2777', '#ea580c', '#dc2626', '#0891b2', '#4f46e5'];
const STATUS_COLORS: Record<string, string> = {
  completed: '#16a34a',
  confirmed: '#2563eb',
  pending: '#f59e0b',
  failed: '#ef4444',
  refunded: '#6366f1'
};

interface SpendingTrend {
  date: string;
  amount: number;
  orders: number;
}

interface TopSupplier {
  supplier_id: string;
  name: string;
  orders_count: number;
  total_spent: number;
  avg_order_value: number;
}

interface PaymentSummary {
  total_paid: number;
  pending_amount: number;
  failed_amount: number;
  payment_methods: Array<{ method: string; count: number; total: number }>;
}

interface Transaction {
  id: string;
  date: string;
  type: string;
  amount: number;
  status: string;
  description: string;
  reference?: string;
}

const BuyerStatsPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  
  const [activeTab, setActiveTab] = useState('panoramica');
  const [period, setPeriod] = useState('30');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Dati statistiche
  const [summary, setSummary] = useState<any>(null);
  const [spendingTrend, setSpendingTrend] = useState<SpendingTrend[]>([]);
  const [topSuppliers, setTopSuppliers] = useState<TopSupplier[]>([]);
  const [ordersByStatus, setOrdersByStatus] = useState<any[]>([]);
  const [monthlySpending, setMonthlySpending] = useState<any[]>([]);
  
  // Dati pagamenti
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);
  const [pendingPayments, setPendingPayments] = useState<any[]>([]);
  
  // Dati transazioni
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch statistiche acquisti
      const [ordersRes, reportRes] = await Promise.all([
        axios.get(`${API}/orders?limit=1000`),
        axios.get(`${API}/orders/report?period=${period === '30' ? 'month' : period === '7' ? 'week' : 'year'}`)
      ]);
      
      const orders = ordersRes.data.orders || [];
      const report = reportRes.data;
      
      // Calcola summary
      const totalSpent = orders.reduce((sum: number, o: any) => sum + (o.total || 0), 0);
      const avgOrderValue = orders.length > 0 ? totalSpent / orders.length : 0;
      
      setSummary({
        total_orders: report.total_orders || orders.length,
        total_spent: report.total_revenue || totalSpent,
        avg_order_value: report.avg_order_value || avgOrderValue,
        orders_this_period: report.total_orders || 0
      });
      
      // Top prodotti come proxy per trend
      setOrdersByStatus(report.by_status || []);
      
      // Calcola trend spesa (simulato dai dati ordini)
      const last30Days = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayOrders = orders.filter((o: any) => o.created_at?.startsWith(dateStr));
        last30Days.push({
          date: dateStr,
          amount: dayOrders.reduce((sum: number, o: any) => sum + (o.total || 0), 0),
          orders: dayOrders.length
        });
      }
      setSpendingTrend(last30Days);
      
      // Calcola top fornitori
      const supplierMap = new Map<string, any>();
      orders.forEach((order: any) => {
        const supplierId = order.seller_id || order.supplier_id;
        const supplierName = order.seller_name || order.supplier_name || 'Fornitore';
        if (supplierId) {
          const existing = supplierMap.get(supplierId) || {
            supplier_id: supplierId,
            name: supplierName,
            orders_count: 0,
            total_spent: 0
          };
          existing.orders_count++;
          existing.total_spent += order.total || 0;
          supplierMap.set(supplierId, existing);
        }
      });
      
      const suppliers = Array.from(supplierMap.values())
        .map(s => ({ ...s, avg_order_value: s.total_spent / s.orders_count }))
        .sort((a, b) => b.total_spent - a.total_spent)
        .slice(0, 5);
      setTopSuppliers(suppliers);
      
      // Calcola spesa mensile
      const monthlyMap = new Map<string, any>();
      orders.forEach((order: any) => {
        if (order.created_at) {
          const month = order.created_at.substring(0, 7);
          const existing = monthlyMap.get(month) || { month, spending: 0, orders: 0 };
          existing.spending += order.total || 0;
          existing.orders++;
          monthlyMap.set(month, existing);
        }
      });
      
      const monthly = Array.from(monthlyMap.values())
        .sort((a, b) => a.month.localeCompare(b.month))
        .slice(-6);
      setMonthlySpending(monthly);
      
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Errore nel caricamento delle statistiche');
    } finally {
      setLoading(false);
    }
  }, [period]);

  const fetchPayments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/buyer-payments`);
      const payments = res.data.payments || [];
      
      // Calcola summary pagamenti
      const paid = payments.filter((p: any) => p.status === 'completed' || p.status === 'paid');
      const pending = payments.filter((p: any) => p.status === 'pending');
      const failed = payments.filter((p: any) => p.status === 'failed');
      
      setPaymentSummary({
        total_paid: paid.reduce((sum: number, p: any) => sum + (p.amount || 0), 0),
        pending_amount: pending.reduce((sum: number, p: any) => sum + (p.amount || 0), 0),
        failed_amount: failed.reduce((sum: number, p: any) => sum + (p.amount || 0), 0),
        payment_methods: []
      });
      
      setPendingPayments(pending.slice(0, 5));
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    setTransactionsLoading(true);
    try {
      const res = await axios.get(`${API}/transactions?limit=50`);
      setTransactions(res.data.transactions || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setTransactionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchPayments();
  }, [fetchStats, fetchPayments]);

  useEffect(() => {
    if (activeTab === 'transazioni') {
      fetchTransactions();
    }
  }, [activeTab, fetchTransactions]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/orders/export/csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `acquisti_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export completato');
    } catch (error) {
      toast.error('Errore nell\'export');
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      completed: { label: 'Completato', variant: 'default' },
      paid: { label: 'Pagato', variant: 'default' },
      confirmed: { label: 'Confermato', variant: 'secondary' },
      pending: { label: 'In attesa', variant: 'outline' },
      failed: { label: 'Fallito', variant: 'destructive' },
      refunded: { label: 'Rimborsato', variant: 'secondary' }
    };
    const { label, variant } = config[status] || { label: status, variant: 'outline' as const };
    return <Badge variant={variant}>{label}</Badge>;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="buyer-stats-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Statistiche</h1>
          <p className="text-muted-foreground mt-1">
            Panoramica dei tuoi acquisti, pagamenti e transazioni
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <Calendar className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Ultima Settimana</SelectItem>
              <SelectItem value="30">Ultimo Mese</SelectItem>
              <SelectItem value="365">Ultimo Anno</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExportCSV} disabled={exporting}>
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className="ml-2 hidden sm:inline">Esporta</span>
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="panoramica" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Panoramica</span>
          </TabsTrigger>
          <TabsTrigger value="pagamenti" className="gap-2">
            <Wallet className="h-4 w-4" />
            <span className="hidden sm:inline">Pagamenti</span>
          </TabsTrigger>
          <TabsTrigger value="transazioni" className="gap-2">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">Transazioni</span>
          </TabsTrigger>
        </TabsList>

        {/* TAB PANORAMICA */}
        <TabsContent value="panoramica" className="space-y-6 mt-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Spesa Totale</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(summary?.total_spent || 0)}</div>
                <p className="text-xs text-muted-foreground">nel periodo selezionato</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Ordini Totali</CardTitle>
                <ShoppingCart className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(summary?.total_orders || 0)}</div>
                <p className="text-xs text-muted-foreground">ordini effettuati</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Ordine Medio</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(summary?.avg_order_value || 0)}</div>
                <p className="text-xs text-muted-foreground">valore medio ordine</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Fornitori Attivi</CardTitle>
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{topSuppliers.length}</div>
                <p className="text-xs text-muted-foreground">con cui hai ordinato</p>
              </CardContent>
            </Card>
          </div>

          {/* Grafici */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Trend Spesa */}
            <Card>
              <CardHeader>
                <CardTitle>Andamento Spesa</CardTitle>
                <CardDescription>Spesa giornaliera negli ultimi 30 giorni</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={spendingTrend}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(v) => new Date(v).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                        className="text-xs"
                      />
                      <YAxis tickFormatter={(v) => `€${v}`} className="text-xs" />
                      <Tooltip 
                        formatter={(value: number) => [formatCurrency(value), 'Spesa']}
                        labelFormatter={(label) => formatDate(label)}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="amount" 
                        stroke="#2563eb" 
                        fill="#2563eb" 
                        fillOpacity={0.2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Spesa Mensile */}
            <Card>
              <CardHeader>
                <CardTitle>Spesa Mensile</CardTitle>
                <CardDescription>Confronto spesa per mese</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlySpending}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="month" 
                        tickFormatter={(v) => {
                          const [year, month] = v.split('-');
                          return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('it-IT', { month: 'short' });
                        }}
                      />
                      <YAxis tickFormatter={(v) => `€${v}`} />
                      <Tooltip formatter={(value: number) => [formatCurrency(value), 'Spesa']} />
                      <Bar dataKey="spending" fill="#16a34a" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Fornitori */}
          <Card>
            <CardHeader>
              <CardTitle>Fornitori Preferiti</CardTitle>
              <CardDescription>I tuoi fornitori con più ordini</CardDescription>
            </CardHeader>
            <CardContent>
              {topSuppliers.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fornitore</TableHead>
                      <TableHead className="text-right">Ordini</TableHead>
                      <TableHead className="text-right">Spesa Totale</TableHead>
                      <TableHead className="text-right">Media Ordine</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topSuppliers.map((supplier, index) => (
                      <TableRow key={supplier.supplier_id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">
                              {index + 1}
                            </span>
                            {supplier.name}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{supplier.orders_count}</TableCell>
                        <TableCell className="text-right">{formatCurrency(supplier.total_spent)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(supplier.avg_order_value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-muted-foreground py-8">Nessun fornitore trovato</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB PAGAMENTI */}
        <TabsContent value="pagamenti" className="space-y-6 mt-6">
          {/* KPI Pagamenti */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Totale Pagato</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(paymentSummary?.total_paid || 0)}
                </div>
                <p className="text-xs text-muted-foreground">pagamenti completati</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">In Attesa</CardTitle>
                <Clock className="h-4 w-4 text-amber-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-600">
                  {formatCurrency(paymentSummary?.pending_amount || 0)}
                </div>
                <p className="text-xs text-muted-foreground">da pagare</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Falliti</CardTitle>
                <XCircle className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {formatCurrency(paymentSummary?.failed_amount || 0)}
                </div>
                <p className="text-xs text-muted-foreground">da riprovare</p>
              </CardContent>
            </Card>
          </div>

          {/* Pagamenti in Attesa */}
          <Card>
            <CardHeader>
              <CardTitle>Pagamenti in Attesa</CardTitle>
              <CardDescription>Pagamenti che richiedono attenzione</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingPayments.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Fornitore</TableHead>
                      <TableHead>Ordine</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                      <TableHead>Stato</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingPayments.map((payment) => (
                      <TableRow key={payment.id || payment._id}>
                        <TableCell>{formatDate(payment.created_at || payment.date)}</TableCell>
                        <TableCell>{payment.seller_name || payment.supplier_name || '-'}</TableCell>
                        <TableCell>#{payment.order_number || payment.order_id?.slice(-8)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(payment.amount)}</TableCell>
                        <TableCell>{getStatusBadge(payment.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <p className="text-muted-foreground">Nessun pagamento in attesa</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB TRANSAZIONI */}
        <TabsContent value="transazioni" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Storico Transazioni</CardTitle>
              <CardDescription>Tutte le tue transazioni recenti</CardDescription>
            </CardHeader>
            <CardContent>
              {transactionsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : transactions.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                      <TableHead>Stato</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((tx) => (
                      <TableRow key={tx.id}>
                        <TableCell>{formatDate(tx.date)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{tx.type}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">{tx.description}</TableCell>
                        <TableCell className="text-right font-medium">
                          <span className={tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {tx.amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(tx.amount))}
                          </span>
                        </TableCell>
                        <TableCell>{getStatusBadge(tx.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <History className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Nessuna transazione trovata</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BuyerStatsPage;
